"use client";

import * as React from "react";

import { useTranslator } from "~/i18n/provider";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { useVariant } from "~/components/product/variant";
import {
  type GalleryMedia,
  mediaForVariant,
  videoSource,
} from "~/components/product/gallery/media-set";
import { Dialog } from "~/components/ui/dialog";
import { StoreImage } from "~/components/ui/store-image";
import { cn } from "~/lib/utils";
import type { GalleryConfig } from "~/server/api/schemas/brand-settings";

type ImageSlide = { id: string; kind: "image"; media: GalleryMedia };
type VideoSlide = {
  id: string;
  kind: "video";
  url: string;
  poster: string | null;
  label: string;
};
type GallerySlide = ImageSlide | VideoSlide;

const ASPECT_CLASS: Record<GalleryConfig["aspectRatio"], string> = {
  square: "aspect-square",
  portrait: "aspect-[4/5]",
  landscape: "aspect-[4/3]",
};

function ImagePlaceholder({ label }: { label: string }) {
  const t = useTranslator();
  return (
    <div
      role="img"
      aria-label={label}
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-surface-2 text-fg-muted"
    >
      <svg
        aria-hidden
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </svg>
      <span className="text-sm">{t("pdp.imageUnavailable")}</span>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg
      aria-hidden
      width="34"
      height="34"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function Video({
  url,
  title,
  poster,
}: {
  url: string;
  title: string;
  poster: string | null;
}) {
  const source = videoSource(url);
  // Shipping the embed to every visitor loads YouTube/Vimeo before any intent,
  // so this is a click-to-load facade: the iframe — with autoplay — only mounts
  // after the click. (It also used to report plays for hosted embeds, which
  // third-party players cannot do through an <iframe> without their JS SDK;
  // that event went to a no-op sink and was removed in P12-02.)
  const [activated, setActivated] = React.useState(false);
  if (source.kind === "embed") {
    if (!activated) {
      return (
        <button
          type="button"
          aria-label={`Play ${title} video`}
          onClick={() => setActivated(true)}
          className="group flex h-full w-full items-center justify-center bg-black focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-accent"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 pl-1 text-black transition-transform group-hover:scale-105">
            <PlayIcon />
          </span>
        </button>
      );
    }
    return (
      <iframe
        src={`${source.src}?autoplay=1`}
        title={`${title} video`}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className="h-full w-full border-0 bg-black"
      />
    );
  }
  return (
    <video
      src={source.src}
      aria-label={`${title} video`}
      poster={poster ?? undefined}
      controls
      preload="metadata"
      playsInline
      className="h-full w-full bg-black object-contain"
    />
  );
}

function ArrowIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      aria-hidden
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      {direction === "left" ? (
        <path d="m15 18-6-6 6-6" />
      ) : (
        <path d="m9 18 6-6-6-6" />
      )}
    </svg>
  );
}

export function Gallery({
  media,
  videoUrl,
  title,
  config,
}: {
  media: GalleryMedia[];
  videoUrl: string | null;
  title: string;
  config: GalleryConfig;
}) {
  const t = useTranslator();
  const { resolvedVariant } = useVariant();
  const videoFlag = useFeatureFlag("pdpGalleryVideo");
  const [current, setCurrent] = React.useState(0);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [brokenImages, setBrokenImages] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [zoomOrigin, setZoomOrigin] = React.useState("50% 50%");
  const [lensActive, setLensActive] = React.useState(false);
  const scrollerRef = React.useRef<HTMLDivElement>(null);

  const activeMedia = React.useMemo(
    () => mediaForVariant(media, resolvedVariant),
    [media, resolvedVariant],
  );
  const slides = React.useMemo<GallerySlide[]>(() => {
    const videoAllowed = videoFlag && config.videoEnabled;
    const result: GallerySlide[] = [];
    for (const item of activeMedia) {
      if (item.kind === "VIDEO") {
        if (videoAllowed && item.url) {
          result.push({
            id: item.id,
            kind: "video",
            url: item.url,
            poster: item.posterUrl,
            label: item.alt ?? "product video",
          });
        }
      } else {
        result.push({ id: item.id, kind: "image", media: item });
      }
    }
    // Hosted product-level video (YouTube/Vimeo, P3-DB03) — only when the set has
    // no uploaded VIDEO asset, so a product never shows two videos.
    const hasMediaVideo = activeMedia.some((item) => item.kind === "VIDEO");
    if (videoAllowed && videoUrl && !hasMediaVideo) {
      result.push({
        id: "product-video",
        kind: "video",
        url: videoUrl,
        poster: null,
        label: "product video",
      });
    }
    return result;
  }, [activeMedia, config.videoEnabled, videoFlag, videoUrl]);

  // The LCP image is the first IMAGE slide (a video may sort ahead of it).
  const firstImageIndex = slides.findIndex((slide) => slide.kind === "image");
  const activeIndex = Math.min(current, Math.max(slides.length - 1, 0));
  const activeSlide = slides[activeIndex];
  const lightboxSlide =
    slides[Math.min(lightboxIndex, Math.max(slides.length - 1, 0))];
  const aspectClass = ASPECT_CLASS[config.aspectRatio];
  const hasSideThumbnails =
    config.thumbPosition === "side" && slides.length > 1;

  React.useEffect(() => {
    setCurrent(0);
    setLensActive(false);
    const scroller = scrollerRef.current;
    // Only when there is somewhere to scroll back FROM. On first hydration
    // the scroller is already at 0, and a programmatic scrollTo issued into a
    // touch gesture that is already in flight makes iOS Safari hand that
    // gesture to the gallery: the page stops, the image "scrolls" instead.
    // Reported on the PDP at load, after P17A-07/10 moved hydration later.
    if (typeof scroller?.scrollTo === "function" && scroller.scrollLeft > 0)
      scroller.scrollTo({ left: 0 });
    // Key on variant id, not imageId: colours can share (or both lack) an image,
    // and a colour change must still reset the viewer to the set's first slide.
  }, [resolvedVariant?.id]);

  // P9-14 · Which index a tap (dot/thumbnail) is scrolling us to. The scroll
  // sync below reaches the same index a moment later; this lets it tell an
  // intentional tap apart from a finger swipe so the telemetry counts only
  // real swipes.
  const programmaticTarget = React.useRef<number | null>(null);
  const scrollFrame = React.useRef<number | null>(null);

  const selectSlide = React.useCallback((index: number) => {
    programmaticTarget.current = index;
    setCurrent(index);
    setLensActive(false);
    const scroller = scrollerRef.current;
    if (typeof scroller?.scrollTo === "function") {
      scroller.scrollTo({
        left: scroller.clientWidth * index,
        behavior: "smooth",
      });
    }
  }, []);

  /**
   * P9-14 · Keep the dots (and the tap-to-zoom target) on the slide the finger
   * landed on. Coalesced into one `requestAnimationFrame` and short-circuited
   * when the index hasn't actually changed: a swipe fires `scroll` many times
   * per second, and re-rendering the gallery on each one is what makes the
   * carousel stutter on a mid-range phone.
   */
  const syncActiveSlide = React.useCallback((scroller: HTMLDivElement) => {
    if (scrollFrame.current !== null) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = null;
      const width = scroller.clientWidth;
      if (width <= 0) return;
      const index = Math.round(scroller.scrollLeft / width);
      setCurrent((prev) => {
        if (prev === index) return prev;
        if (programmaticTarget.current === index) {
          programmaticTarget.current = null; // arrived from a tap, not a swipe
        } else {
        }
        return index;
      });
    });
  }, []);

  React.useEffect(
    () => () => {
      if (scrollFrame.current !== null)
        cancelAnimationFrame(scrollFrame.current);
    },
    [],
  );

  const moveLightbox = React.useCallback(
    (amount: number) => {
      if (slides.length < 2) return;
      setLightboxIndex(
        (value) => (value + amount + slides.length) % slides.length,
      );
    },
    [slides.length],
  );

  React.useEffect(() => {
    if (!lightboxOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        moveLightbox(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        moveLightbox(1);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen, moveLightbox]);

  function markBroken(id: string) {
    setBrokenImages((currentSet) => new Set(currentSet).add(id));
  }

  function openLightbox() {
    if (!activeSlide || activeSlide.kind !== "image") return;
    setLightboxIndex(activeIndex);
    setLightboxOpen(true);
  }

  function updateLens(event: React.PointerEvent<HTMLButtonElement>) {
    if (config.zoomStyle !== "lens" || event.pointerType === "touch") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;
    setZoomOrigin(`${x}% ${y}%`);
    setLensActive(true);
  }

  const thumbnailList = slides.length > 1 && (
    <div
      aria-label={t("pdp.mediaThumbnails")}
      className={cn(
        "hidden gap-2 md:flex",
        config.thumbPosition === "side"
          ? "max-h-[42rem] flex-col overflow-y-auto"
          : "mt-3 overflow-x-auto",
      )}
    >
      {slides.map((slide, index) => (
        <button
          key={slide.id}
          type="button"
          aria-label={`Show ${slide.kind === "video" ? slide.label : (slide.media.alt ?? `${title} image ${index + 1}`)}`}
          aria-current={index === activeIndex ? "true" : undefined}
          onClick={() => selectSlide(index)}
          className={cn(
            "relative aspect-square w-16 shrink-0 overflow-hidden rounded-[var(--radius-md)] border-2 bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            index === activeIndex
              ? "border-accent"
              : "border-transparent hover:border-border",
          )}
        >
          {slide.kind === "image" &&
          slide.media.url &&
          !brokenImages.has(slide.media.id) ? (
            <StoreImage
              src={slide.media.url}
              alt=""
              fill
              preset="thumb"
              loading="lazy"
              onError={() => markBroken(slide.media.id)}
              className="object-cover"
            />
          ) : slide.kind === "video" ? (
            <>
              {slide.poster && !brokenImages.has(slide.id) ? (
                <StoreImage
                  src={slide.poster}
                  alt=""
                  fill
                  preset="thumb"
                  loading="lazy"
                  onError={() => markBroken(slide.id)}
                  className="object-cover"
                />
              ) : (
                <span
                  aria-hidden
                  className="flex h-full items-center justify-center text-xs font-semibold text-fg"
                >
                  {t("pdp.video")}
                </span>
              )}
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 pl-0.5 text-white">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
            </>
          ) : (
            <ImagePlaceholder label={t("pdp.imageUnavailable")} />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <section data-testid="pdp-gallery" aria-label={t("pdp.gallery")} className="min-w-0">
      <div
        data-testid="pdp-gallery-layout"
        className={cn(
          "min-w-0",
          hasSideThumbnails &&
            "md:grid md:grid-cols-[4rem_minmax(0,1fr)] md:gap-3",
        )}
      >
        {hasSideThumbnails && thumbnailList}
        <div className="min-w-0">
          <div
            ref={scrollerRef}
            onScroll={(event) => syncActiveSlide(event.currentTarget)}
            className={cn(
              // overflow-y-hidden: `overflow-x: auto` alone makes overflow-y compute to
              // auto too, so iOS treats the strip as a vertical scroll container and a
              // vertical drag that starts on it can be claimed before the page gets it.
              // overscroll-x-contain: a swipe past the last slide stays in the strip.
              "flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain rounded-[var(--radius-lg)] bg-surface-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:block md:overflow-hidden",
              aspectClass,
            )}
          >
            {slides.length === 0 ? (
              <ImagePlaceholder label={`${title} image unavailable`} />
            ) : (
              slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={cn(
                    "relative h-full w-full shrink-0 snap-center overflow-hidden",
                    index !== activeIndex && "md:hidden",
                  )}
                >
                  {slide.kind === "video" ? (
                    <Video
                      url={slide.url}
                      title={title}
                      poster={slide.poster}
                    />
                  ) : (
                    <button
                      type="button"
                      aria-label={`Open ${slide.media.alt ?? `${title} image ${index + 1}`} in fullscreen`}
                      onClick={openLightbox}
                      onPointerMove={updateLens}
                      onPointerLeave={() => setLensActive(false)}
                      // pan-x = swipe the carousel, pan-y = let a vertical drag
                      // that starts on the image still scroll the page (without
                      // pan-y the image traps vertical scroll on mobile), plus
                      // pinch-zoom. Tap still opens the lightbox.
                      className="relative h-full w-full cursor-zoom-in overflow-hidden [touch-action:pan-x_pan-y_pinch-zoom] focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-accent"
                    >
                      {slide.media.url && !brokenImages.has(slide.media.id) ? (
                        <StoreImage
                          src={slide.media.url}
                          alt={slide.media.alt ?? title}
                          fill
                          preset={hasSideThumbnails ? "pdpGallerySide" : "pdpGallery"}
                          priority={index === firstImageIndex}
                          loading={
                            index === firstImageIndex ? undefined : "lazy"
                          }
                          blurDataUrl={slide.media.blurDataUrl}
                          draggable={false}
                          onError={() => markBroken(slide.media.id)}
                          className={cn(
                            // Keep the full source visible at every viewport. A
                            // merchant may upload portrait, square, or unusually
                            // framed product photos; cropping can hide product
                            // details and cannot be inferred from brand ratio.
                            "object-contain transition-transform duration-150",
                            lensActive &&
                              index === activeIndex &&
                              "md:scale-[1.8]",
                          )}
                          style={{ transformOrigin: zoomOrigin }}
                        />
                      ) : (
                        <ImagePlaceholder
                          label={`${title} image unavailable`}
                        />
                      )}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {slides.length > 1 && (
            <div
              role="group"
              aria-label={t("pdp.mediaPosition")}
              className="mt-3 flex items-center justify-center gap-2 md:hidden"
            >
              {/* A long, unbroken dot row sets the grid's minimum width and
                  pushes the page beyond a phone viewport. Keep large sets
                  navigable with controls whose width is independent of count. */}
              {slides.length > 8 ? (
                <>
                  <button
                    type="button"
                    aria-label={t("pdp.previousMedia")}
                    disabled={activeIndex === 0}
                    onClick={() => selectSlide(activeIndex - 1)}
                    className="flex size-11 items-center justify-center rounded-full text-fg disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    <ArrowIcon direction="left" />
                  </button>
                  <span aria-live="polite" aria-atomic="true" className="min-w-16 text-center text-sm tabular-nums text-fg-muted">
                    {activeIndex + 1} / {slides.length}
                  </span>
                  <button
                    type="button"
                    aria-label={t("pdp.nextMedia")}
                    disabled={activeIndex === slides.length - 1}
                    onClick={() => selectSlide(activeIndex + 1)}
                    className="flex size-11 items-center justify-center rounded-full text-fg disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-accent"
                  >
                    <ArrowIcon direction="right" />
                  </button>
                </>
              ) : slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  aria-label={`Go to media ${index + 1}`}
                  aria-current={index === activeIndex ? "true" : undefined}
                  onClick={() => selectSlide(index)}
                  className={cn(
                    "h-2 rounded-full transition-[width]",
                    index === activeIndex ? "w-6 bg-accent" : "w-2 bg-border",
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {config.thumbPosition === "bottom" && thumbnailList}

      <Dialog
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        ariaLabel={`${title} image viewer`}
        className="h-[calc(100dvh-2rem)] max-w-none overflow-hidden border-0 bg-black p-0 text-white"
      >
        <button
          type="button"
          aria-label={t("pdp.closeViewer")}
          onClick={() => setLightboxOpen(false)}
          className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-2xl text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <span aria-hidden>&times;</span>
        </button>
        {slides.length > 1 && (
          <>
            <button
              type="button"
              aria-label={t("pdp.previousMedia")}
              onClick={() => moveLightbox(-1)}
              className="absolute left-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white focus-visible:outline-2 focus-visible:outline-white"
            >
              <ArrowIcon direction="left" />
            </button>
            <button
              type="button"
              aria-label={t("pdp.nextMedia")}
              onClick={() => moveLightbox(1)}
              className="absolute right-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white focus-visible:outline-2 focus-visible:outline-white"
            >
              <ArrowIcon direction="right" />
            </button>
          </>
        )}
        <div className="flex h-full w-full items-center justify-center p-4 sm:p-12">
          {lightboxSlide?.kind === "image" &&
          lightboxSlide.media.url &&
          !brokenImages.has(lightboxSlide.media.id) ? (
            <div className="relative h-full w-full [touch-action:pinch-zoom]">
              <StoreImage
                src={lightboxSlide.media.url}
                alt={lightboxSlide.media.alt ?? title}
                fill
                preset="hero"
                loading="lazy"
                draggable={false}
                onError={() => markBroken(lightboxSlide.media.id)}
                className="object-contain"
              />
            </div>
          ) : lightboxSlide?.kind === "video" ? (
            <Video
              url={lightboxSlide.url}
              title={title}
              poster={lightboxSlide.poster}
            />
          ) : (
            <ImagePlaceholder label={`${title} image unavailable`} />
          )}
        </div>
      </Dialog>
    </section>
  );
}
